import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import type {
  VoteAnalysisResponse,
  VotingProfile,
  AiAnalysisResponse,
} from '../types/Vote.api.type';

interface Props {
  data: VoteAnalysisResponse;
  profile?: VotingProfile;
  aiAnalysis?: AiAnalysisResponse;
  isAiLoading?: boolean;
  isAuthenticated?: boolean;
}

const GENDER_LABELS: Record<string, string> = { male: '남성', female: '여성', unknown: '미입력' };
const GENDER_COLORS: Record<string, string> = { male: '#3B82F6', female: '#EC4899', unknown: '#9CA3AF' };
const AGE_LABELS: Record<string, string> = {
  teens: '10대', twenties: '20대', thirties: '30대',
  forties: '40대', fifties: '50대', sixtyPlus: '60대+', unknown: '미입력',
  TEENS: '10대', TWENTIES: '20대', THIRTIES: '30대',
  FORTIES: '40대', FIFTIES: '50대', SIXTY_PLUS: '60대+',
};
const AGE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#F97316', '#EF4444', '#8B5CF6', '#9CA3AF'];

const TAG_STYLES: Record<string, { text: string; bg: string }> = {
  MAJORITY: { text: '다수파', bg: 'bg-blue-100 text-blue-700' },
  MINORITY: { text: '소수파', bg: 'bg-amber-100 text-amber-700' },
  EVEN: { text: '팽팽한 선택', bg: 'bg-gray-100 text-gray-600' },
};

const TAG_COLORS: Record<string, string> = {
  MAJORITY: '#3B82F6',
  MINORITY: '#F59E0B',
  EVEN: '#6B7280',
};

const PERSONALITY: Record<string, { text: string; emoji: string; gradient: string }> = {
  INDEPENDENT: { text: '독립적 사고형', emoji: '🎯', gradient: 'from-orange-50 to-orange-100' },
  MAINSTREAM: { text: '대세 추종형', emoji: '🌊', gradient: 'from-blue-50 to-blue-100' },
  BALANCED: { text: '균형잡힌 시각', emoji: '⚖️', gradient: 'from-green-50 to-green-100' },
};

const Accordion: React.FC<{ title: string; defaultOpen?: boolean; children: React.ReactNode }> = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-3 px-1 text-left hover:bg-gray-50 transition-colors">
        <span className="text-sm font-semibold text-gray-800">{title}</span>
        <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
      </button>
      {open && <div className="pb-4 px-1">{children}</div>}
    </div>
  );
};

const PercentBar: React.FC<{ label: string; count: number; total: number; color: string; highlight?: boolean }> = ({ label, count, total, color, highlight }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1">
      <span className={`text-xs w-10 shrink-0 ${highlight ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{label}{highlight ? ' ◀' : ''}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-gray-500 w-16 text-right shrink-0">{count}명 ({pct}%)</span>
    </div>
  );
};

const GenderChart: React.FC<{ dist: { male: number; female: number; unknown: number }; highlightGender?: string }> = ({ dist, highlightGender }) => {
  const total = dist.male + dist.female + dist.unknown;
  return (
    <div>
      {Object.entries({ male: dist.male, female: dist.female, unknown: dist.unknown }).map(([key, count]) => (
        <PercentBar key={key} label={GENDER_LABELS[key]} count={count} total={total} color={GENDER_COLORS[key]}
          highlight={highlightGender === key.toUpperCase()} />
      ))}
    </div>
  );
};

const AgeGroupChart: React.FC<{ dist: Record<string, number>; highlightAge?: string }> = ({ dist, highlightAge }) => {
  const keys = ['teens', 'twenties', 'thirties', 'forties', 'fifties', 'sixtyPlus', 'unknown'];
  const chartData = keys.map((key, i) => ({
    label: highlightAge === key.toUpperCase() ? `${AGE_LABELS[key]} ◀` : AGE_LABELS[key],
    value: dist[key] ?? 0,
    color: AGE_COLORS[i],
  }));
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={chartData} margin={{ top: 15, right: 5, left: -20, bottom: 0 }}>
        <XAxis dataKey="label" stroke="#9CA3AF" style={{ fontSize: '11px' }} />
        <YAxis stroke="#9CA3AF" style={{ fontSize: '11px' }} width={35} allowDecimals={false} />
        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '12px' }}
          formatter={(value) => [`${value}명`, '투표수']} />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const VoteAnalysisSection: React.FC<Props> = ({ data, profile, aiAnalysis, isAiLoading, isAuthenticated = false }) => {
  if (!data || !data.optionAnalysis) return null;

  const mc = data.myChoice;
  const si = data.surveyInsight;

  return (
    <div className="mt-4 bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-bold text-gray-900 mb-1">분석 결과</h4>
      <p className="text-xs text-gray-400 mb-3">총 {data.totalVotes}명</p>

      {/* S6-23: AI 한 줄 결론 (맨 위) */}
      {aiAnalysis?.surveyInsightText && (
        <div className="mb-3 p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 border-l-[3px] border-blue-500">
          <p className="text-xs text-gray-800 leading-relaxed">
            ✨ {aiAnalysis.surveyInsightText}
          </p>
        </div>
      )}
      {isAiLoading && !aiAnalysis?.surveyInsightText && (
        <div className="mb-3 p-3 rounded-xl bg-gray-100">
          <p className="text-xs text-gray-400">✨ AI가 분석하고 있어요...</p>
        </div>
      )}

      {/* ===== 섹션 1: 나의 선택 ===== */}
      {mc && (
        <div className="bg-blue-50 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">나의 선택</p>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm font-bold text-gray-900">{mc.optionText}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TAG_STYLES[mc.tag]?.bg ?? 'bg-gray-100'}`}>
              {TAG_STYLES[mc.tag]?.text ?? mc.tag}
            </span>
          </div>
          {/* S6-23: 퍼센타일 강조 카드 */}
          <div className={`p-2.5 rounded-lg mb-2 ${
            mc.tag === 'MINORITY' ? 'bg-amber-50' : mc.tag === 'MAJORITY' ? 'bg-blue-100/50' : 'bg-gray-100'
          }`}>
            <p className="text-xs font-bold" style={{ color: TAG_COLORS[mc.tag] ?? '#6B7280' }}>
              {mc.tag === 'MINORITY'
                ? `전체 ${mc.myOptionPercent}%만 선택한 의견`
                : mc.tag === 'MAJORITY'
                  ? `전체 ${mc.myOptionPercent}%가 선택한 다수 의견`
                  : `전체 ${mc.myOptionPercent}%가 선택 — 팽팽한 결과`
              }
            </p>
          </div>
          {mc.myGender && mc.myGender !== 'UNKNOWN' && mc.sameGenderTotal > 0 && (
            <p className="text-xs text-gray-600 mb-0.5">
              👤 같은 {mc.myGender === 'MALE' ? '남성' : '여성'} 중에선 {mc.sameGenderMyOptionPct}%가 같은 선택
            </p>
          )}
          {mc.myAgeGroup && mc.myAgeGroup !== 'UNKNOWN' && mc.sameAgeTotal > 0 && (
            <p className="text-xs text-gray-600">
              📅 같은 {AGE_LABELS[mc.myAgeGroup] ?? mc.myAgeGroup} 중에선 {mc.sameAgeMyOptionPct}%가 같은 선택
            </p>
          )}
        </div>
      )}

      {!isAuthenticated && !mc && (
        <div className="bg-gray-100 rounded-lg p-3 mb-4 text-center">
          <p className="text-xs text-gray-500">로그인하면 나만의 분석을 볼 수 있어요</p>
        </div>
      )}

      {/* ===== 섹션 2: 설문 인사이트 (AI 텍스트는 상단으로 이동) ===== */}
      {si && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <p className="text-xs font-semibold text-gray-700 mb-2">설문 인사이트</p>
          <div className="space-y-1.5">
            {si.genderGapIndex >= 0.3 && (
              <p className="text-xs text-gray-700">
                🔥 남녀 의견 갈림 — {si.genderGapOption}에서 남성 {si.genderGapMalePct}% vs 여성 {si.genderGapFemalePct}%
              </p>
            )}
            {si.ageGapIndex >= 0.3 && si.youngestGroup && si.oldestGroup && si.youngestGroup !== si.oldestGroup && (
              <p className="text-xs text-gray-700">
                📊 세대 차이 — {AGE_LABELS[si.youngestGroup]}와 {AGE_LABELS[si.oldestGroup]}의 선택이 다름
              </p>
            )}
            {si.consensusIndex >= 0.8 && (
              <p className="text-xs text-gray-700">
                🤝 높은 합의 — 전체 {Math.round(si.consensusIndex * 100)}%가 같은 선택
              </p>
            )}
            {si.opinionShifted && si.earlyLeader && si.lateLeader && (
              <p className="text-xs text-gray-700">
                📈 의견 변화 — 초반 "{si.earlyLeader}" → 후반 "{si.lateLeader}"
              </p>
            )}
            {si.genderGapIndex < 0.3 && si.ageGapIndex < 0.3 && si.consensusIndex < 0.8 && !si.opinionShifted && (
              <p className="text-xs text-gray-500">비교적 고른 분포를 보여요</p>
            )}
          </div>
        </div>
      )}

      {/* ===== 섹션 3: 상세 차트 ===== */}
      <Accordion title="성별 분포">
        <GenderChart dist={data.genderDistribution} highlightGender={mc?.myGender} />
      </Accordion>

      <Accordion title="연령대 분포">
        <AgeGroupChart dist={data.ageGroupDistribution as unknown as Record<string, number>} highlightAge={mc?.myAgeGroup} />
      </Accordion>

      <Accordion title="선택지별 성별">
        {data.optionAnalysis.map((opt) => (
          <div key={opt.optionId} className="mb-3 last:mb-0">
            <p className="text-xs font-medium text-gray-700 mb-1">
              {opt.optionText} <span className="text-gray-400">({opt.voteCount}명)</span>
            </p>
            <GenderChart dist={opt.genderBreakdown} />
          </div>
        ))}
      </Accordion>

      <Accordion title="선택지별 연령대">
        {data.optionAnalysis.map((opt) => (
          <div key={opt.optionId} className="mb-3 last:mb-0">
            <p className="text-xs font-medium text-gray-700 mb-1">
              {opt.optionText} <span className="text-gray-400">({opt.voteCount}명)</span>
            </p>
            <AgeGroupChart dist={opt.ageGroupBreakdown as unknown as Record<string, number>} />
          </div>
        ))}
      </Accordion>

      {/* ===== 섹션 4: 나의 투표 성향 (S6-23 비주얼 강화) ===== */}
      {isAuthenticated && profile && profile.totalSurveys >= 5 && (
        <div className={`rounded-2xl p-5 mt-4 bg-gradient-to-br ${PERSONALITY[profile.personalityTag]?.gradient ?? 'from-gray-50 to-gray-100'}`}>
          {/* 성향 카드 헤더 */}
          <div className="text-center mb-4">
            <div className="text-4xl mb-2">
              {PERSONALITY[profile.personalityTag]?.emoji ?? '🎯'}
            </div>
            <p className="text-base font-bold text-gray-900">
              {PERSONALITY[profile.personalityTag]?.text ?? profile.personalityTag}
            </p>
          </div>

          {/* 핵심 수치 3칸 */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="text-center py-2.5 px-1 bg-white/70 rounded-lg">
              <p className="text-sm font-bold text-gray-900">{Math.round(profile.minorityRate * 100)}%</p>
              <p className="text-[10px] text-gray-500">소수 선택</p>
            </div>
            <div className="text-center py-2.5 px-1 bg-white/70 rounded-lg">
              <p className="text-sm font-bold text-gray-900">{Math.round(profile.groupAlignRate * 100)}%</p>
              <p className="text-[10px] text-gray-500">동조율</p>
            </div>
            <div className="text-center py-2.5 px-1 bg-white/70 rounded-lg">
              <p className="text-sm font-bold text-gray-900">{profile.totalSurveys}개</p>
              <p className="text-[10px] text-gray-500">참여 설문</p>
            </div>
          </div>

          {/* 카테고리 */}
          {profile.categoryStats.length > 0 && (
            <p className="text-xs text-gray-600 mb-3">
              자주 참여: {profile.categoryStats.slice(0, 3).map(c => `${c.category}(${c.count}회)`).join(', ')}
            </p>
          )}

          {/* AI 개인화 분석 */}
          {aiAnalysis?.personalityText && (
            <div className="p-3 rounded-xl bg-white/80 border-l-[3px] border-blue-500">
              <p className="text-xs text-gray-800 leading-relaxed">✨ {aiAnalysis.personalityText}</p>
            </div>
          )}
          {isAiLoading && !aiAnalysis?.personalityText && (
            <div className="p-3 rounded-xl bg-white/50">
              <p className="text-xs text-gray-400">✨ AI가 당신의 성향을 분석하고 있어요...</p>
            </div>
          )}
        </div>
      )}

      {isAuthenticated && profile && profile.totalSurveys < 5 && (
        <div className="bg-gray-100 rounded-lg p-3 mt-4 text-center">
          <p className="text-xs text-gray-500">5개 이상 설문에 참여하면 투표 성향 분석이 시작돼요</p>
        </div>
      )}
    </div>
  );
};
